import axios from "axios";

// URL do backend com fallback
const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';

const api = axios.create({
	baseURL: backendUrl,
	withCredentials: true,
});

export const openApi = axios.create({
	baseURL: backendUrl
});

// Flag para evitar múltiplas tentativas de refresh simultâneas
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
	failedQueue.forEach(prom => {
		if (error) {
			prom.reject(error);
		} else {
			prom.resolve(token);
		}
	});

	failedQueue = [];
};

// Interceptor para atualizar token automaticamente
api.interceptors.response.use(
	response => response,
	async error => {
		const originalRequest = error.config;

		// Se o erro não for 401, rejeitar normalmente
		if (!error.response || error.response.status !== 401) {
			return Promise.reject(error);
		}

		// IMPORTANTE: Não interceptar rotas de autenticação (evita loop infinito)
		if (originalRequest.url?.includes('/auth/login') ||
		    originalRequest.url?.includes('/auth/refresh_token') ||
		    originalRequest.url?.includes('/auth/signup')) {
			return Promise.reject(error);
		}

		// Se já tentamos refresh nesta request, fazer logout
		if (originalRequest._retry) {
			console.warn('🔄 Token refresh falhou, redirecionando para login');
			// Limpar auth e redirecionar para login
			localStorage.removeItem("token");
			localStorage.removeItem("companyId");
			localStorage.removeItem("userId");
			
			// Mostrar mensagem para o usuário
			if (window.location.pathname !== '/login') {
				window.location.href = "/login";
			}
			return Promise.reject(error);
		}

		// Se estamos refreshing, adicionar à fila de espera
		if (isRefreshing) {
			return new Promise((resolve, reject) => {
				failedQueue.push({ resolve, reject });
			})
				.then(token => {
					originalRequest.headers.Authorization = `Bearer ${token}`;
					return api(originalRequest);
				})
				.catch(err => Promise.reject(err));
		}

		originalRequest._retry = true;
		isRefreshing = true;

		try {
			// Tentar fazer refresh do token
			const { data } = await api.post("/auth/refresh_token");

			const { token } = data;

			// Atualizar token no localStorage
			localStorage.setItem("token", JSON.stringify(token));

			// Atualizar header da request original
			originalRequest.headers.Authorization = `Bearer ${token}`;

			// Processar fila de requests que estavam esperando
			processQueue(null, token);

			// Retry da request original
			return api(originalRequest);
		} catch (refreshError) {
			console.warn('🔄 Refresh token falhou, fazendo logout');
			// Se refresh falhar, fazer logout
			processQueue(refreshError, null);

			localStorage.removeItem("token");
			localStorage.removeItem("companyId");
			localStorage.removeItem("userId");

			// Mostrar mensagem para o usuário
			if (window.location.pathname !== '/login') {
				window.location.href = "/login";
			}

			return Promise.reject(refreshError);
		} finally {
			isRefreshing = false;
		}
	}
);

// Interceptor para adicionar token em todas as requests
api.interceptors.request.use(
	config => {
		// IMPORTANTE: Não enviar token para rotas de autenticação
		if (config.url?.includes('/auth/login') ||
		    config.url?.includes('/auth/refresh_token') ||
		    config.url?.includes('/auth/signup')) {
			return config;
		}

		const token = localStorage.getItem("token");
		if (token) {
			const tokenValue = JSON.parse(token);
			config.headers.Authorization = `Bearer ${tokenValue}`;
		}
		return config;
	},
	error => Promise.reject(error)
);

export default api;